import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);

  // Search, filter, and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(10);
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  useEffect(() => {
    fetchTransactions();
  }, []);

  // Filter transactions based on search term, status, and plan
  useEffect(() => {
    let filtered = transactions;

    // Search filter
    if (searchTerm.trim()) {
      const lowercaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(transaction => 
        transaction.userId?.username?.toLowerCase().includes(lowercaseSearch) ||
        transaction.userId?.email?.toLowerCase().includes(lowercaseSearch) ||
        transaction.paymentId?.toLowerCase().includes(lowercaseSearch) ||
        transaction.paymentMethod?.toLowerCase().includes(lowercaseSearch)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(transaction => transaction.status === statusFilter);
    }

    // Plan filter
    if (planFilter !== 'all') {
      filtered = filtered.filter(transaction => transaction.plan === planFilter);
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, planFilter, transactions]);

  const fetchTransactions = async () => {
    try {
      const res = await axios.get('/api/admin/transactions');
      setTransactions(res.data);
      setFilteredTransactions(res.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  // Get current transactions for pagination
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-900/50 text-green-200';
      case 'pending':
        return 'bg-yellow-900/50 text-yellow-200';
      case 'failed':
        return 'bg-red-900/50 text-red-200';
      default:
        return 'bg-gray-900/50 text-gray-200';
    }
  };

  const totalRevenue = filteredTransactions
    .filter(t => t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Transactions</h1>
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-gray-400 text-sm">Revenus Totaux (Filtrés)</p>
            <p className="text-2xl font-bold text-white">{totalRevenue.toFixed(2)}€</p>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg overflow-hidden">
          {/* Search and Filters */}
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search Bar */}
              <div className="md:col-span-1">
                <input
                  type="text"
                  placeholder="Rechercher par utilisateur, ID de paiement, méthode..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="completed">Terminé</option>
                  <option value="pending">En attente</option>
                  <option value="failed">Échoué</option>
                </select>
              </div>

              {/* Plan Filter */}
              <div>
                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                >
                  <option value="all">Tous les plans</option>
                  <option value="1month">1 Mois</option>
                  <option value="6months">6 Mois</option>
                  <option value="12months">12 Mois</option>
                </select>
              </div>
            </div>
            <div className="text-gray-400 text-sm">
              {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-white">Date</th>
                  <th className="px-6 py-4 text-left text-white">Utilisateur</th>
                  <th className="px-6 py-4 text-left text-white">Plan</th>
                  <th className="px-6 py-4 text-left text-white">Montant</th>
                  <th className="px-6 py-4 text-left text-white">Méthode</th>
                  <th className="px-6 py-4 text-left text-white">Statut</th>
                  <th className="px-6 py-4 text-left text-white">ID de Paiement</th>
                </tr>
              </thead>
              <tbody>
                {currentTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                      {searchTerm || statusFilter !== 'all' || planFilter !== 'all' 
                        ? 'Aucune transaction trouvée correspondant à vos filtres.' 
                        : 'Aucune transaction disponible.'}
                    </td>
                  </tr>
                ) : (
                  currentTransactions.map((transaction) => (
                    <tr key={transaction._id} className="border-b border-gray-700">
                      <td className="px-6 py-4 text-gray-300">{formatDate(transaction.createdAt)}</td>
                      <td className="px-6 py-4 text-white">
                        {transaction.userId?.username || transaction.userId?.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-gray-300">{transaction.plan}</td>
                      <td className="px-6 py-4 text-white">{transaction.amount} {transaction.currency}</td>
                      <td className="px-6 py-4 text-gray-300 capitalize">{transaction.paymentMethod === 'cryptocurrency' ? 'Cryptomonnaie' : transaction.paymentMethod === 'card' ? 'Carte' : transaction.paymentMethod}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded text-sm ${getStatusColor(transaction.status)}`}>
                          {transaction.status === 'completed' ? 'Terminé' : transaction.status === 'pending' ? 'En attente' : transaction.status === 'failed' ? 'Échoué' : transaction.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-sm font-mono">
                        {transaction.paymentId.substring(0, 20)}...
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <div className="text-gray-400 text-sm">
                Affichage de {indexOfFirstTransaction + 1} à {Math.min(indexOfLastTransaction, filteredTransactions.length)} sur {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Précédent
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`px-4 py-2 rounded-lg transition ${
                          currentPage === pageNum
                            ? 'bg-white text-gray-900 font-semibold'
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transactions;
